import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { adminApi } from "../../utils/axiosConfig";
import DeleteProduct from "./DeleteProduct";
import ModifyProduct from "./ModifyProduct";
import AddProduct from "./AddProduct";
import ThreeModelViewer from "../ThreeModelViewer";
import { FaStar, FaSearch, FaChevronDown, FaChevronUp, FaTag } from "react-icons/fa";

import ReactMarkdown from "react-markdown";

const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

const buildImageUrl = (filename) => {
  if (!filename || typeof filename !== "string") return "";

  // URL complète détectée, on la retourne brute
  if (/^https?:\/\//i.test(filename)) {
    return filename;
  }

  // Sinon on construit URL locale
  let cleanFilename = filename.startsWith("/") ? filename.slice(1) : filename;

  if (cleanFilename.startsWith("public/")) {
    cleanFilename = cleanFilename.slice("public/".length);
  }

  if (!cleanFilename.startsWith("uploads/")) {
    cleanFilename = `uploads/${cleanFilename}`;
  }

  return `${apiUrl}/${cleanFilename}`.replace(/([^:]\/)\/+/g, "$1");
};


export default function Products() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      setMessage(null);
      const res = await adminApi.get(`/api/admin/products`);
      const productsData = res.data.products || res.data;
      setProducts(productsData);
      setFilteredProducts(productsData);
    } catch (err) {
      setMessage("Erreur lors du chargement des produits");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const togglePopular = async (productId, currentStatus) => {
    try {
      await adminApi.put(
        `/api/admin/products/${productId}/popular`,
        { isPopular: !currentStatus }
      );
      fetchProducts();
    } catch (err) {
      console.error("Erreur lors du changement du statut populaire", err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter((product) =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  if (loading && products.length === 0) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement des produits...</p>
      </div>
    );
  }

  if (message) {
    return <div className="error-message">{message}</div>;
  }

  return (
    <div className="products-admin-container">
      <div className="products-header">
        <h2>Gestion des Produits</h2>
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher un produit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="products-count">
          {filteredProducts.length} produit
          {filteredProducts.length !== 1 ? "s" : ""} trouvé
          {filteredProducts.length !== 1 ? "s" : ""}
        </div>
      </div>

      <div className="products-grid">
        {filteredProducts.map((p) => (
          <ProductCard
            key={p._id}
            product={p}
            fetchProducts={fetchProducts}
            togglePopular={togglePopular}
          />
        ))}
      </div>
    </div>
  );
}

const ProductCard = ({ product, fetchProducts, togglePopular }) => {
  const [expanded, setExpanded] = useState(false);
  const hasPromo = product.price.promoPrice && product.price.promoPrice < product.price.originalPrice;

  return (
    <div className={`product-card ${expanded ? "expanded" : ""}`}>
      <div
        className="product-card-header"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="product-basic-info">
          <h3>{product.name}</h3>
          <div className="product-meta">
            <div className="price-container">
              {hasPromo ? (
                <>
                  <div className="original-price">
                    {product.price.originalPrice} MAD
                  </div>
                  <div className="promo-price-container">
                    <span className="promo-price">
                      {product.price.promoPrice.toFixed(2)} MAD
                    </span>
                    <span className="promo-badge">
                      <FaTag className="tag-icon" />
                      {product.promo}%
                    </span>
                  </div>
                </>
              ) : (
                <div className="normal-price">
                  {product.price.originalPrice} MAD
                </div>
              )}
            </div>
            <span className="product-type">
              {formatProductType(product.type)}
            </span>
          </div>
        </div>

        <div className="product-actions-header">
          <button
            className={`popular-btn ${product.isPopular ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              togglePopular(product._id, product.isPopular);
            }}
            title={
              product.isPopular ? "Retirer des favoris" : "Ajouter aux favoris"
            }
          >
            <FaStar />
          </button>

          <div className="expand-btn">
            {expanded ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="product-card-details">
          <div className="product-description">
            {product.description ? (
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <h1
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        marginBottom: "1rem",
                      }}
                      {...props}
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      style={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        marginBottom: "0.75rem",
                      }}
                      {...props}
                    />
                  ),
                  p: ({ node, ...props }) => (
                    <p
                      style={{ marginBottom: "0.8rem", color: "#3e2c1c" }}
                      {...props}
                    />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul
                      style={{
                        paddingLeft: "1.5rem",
                        marginBottom: "1rem",
                        listStyleType: "disc",
                        color: "#3e2c1c",
                      }}
                      {...props}
                    />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol
                      style={{
                        paddingLeft: "1.5rem",
                        marginBottom: "1rem",
                        listStyleType: "decimal",
                        color: "#3e2c1c",
                      }}
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => (
                    <li style={{ marginBottom: "0.4rem" }} {...props} />
                  ),
                }}
              >
                {product.description}
              </ReactMarkdown>
            ) : (
              <em className="no-description">Aucune description disponible</em>
            )}
          </div>

          <div className="variants-section">
            <h4>Variantes</h4>
            {product.variantes && product.variantes.length > 0 ? (
              product.variantes.map((variant, i) => (
                <VariantCard
                  key={i}
                  variant={variant}
                  productName={product.name}
                />
              ))
            ) : (
              <p className="no-variants">Aucune variante disponible</p>
            )}
          </div>

          <div className="product-actions-footer">
            <ModifyProduct product={product} onProductUpdated={fetchProducts} />
            <DeleteProduct
              productId={product._id}
              onProductDeleted={fetchProducts}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const VariantCard = ({ variant, productName }) => {
  return (
    <div
      className="variant-card"
      style={{ borderLeftColor: getColorHex(variant.couleur) }}
    >
      <div className="variant-header">
        <h5>Couleur: {variant.couleur}</h5>
      </div>

      <div className="variant-content">
        <div className="stock-section">
          <h6>Stock disponible</h6>
          <div className="stock-grid">
            {variant.stock && variant.stock.length > 0 ? (
              variant.stock.map((item) => (
                <div key={item._id || item.taille} className="stock-item">
                  <span>
                    Taille: <strong>{item.taille}</strong>
                  </span>
                  <span>
                    Quantité: <strong>{item.quantity}</strong>
                  </span>
                </div>
              ))
            ) : (
              <p className="no-stock">Aucun stock disponible</p>
            )}
          </div>
        </div>

        <div className="variant-media">
          <h6>Images</h6>
          <div className="media-grid">
            {variant.images && variant.images.length > 0 ? (
              variant.images.map((filename, idx) => {
                if (!filename || typeof filename !== "string") return null;
                const url = buildImageUrl(filename);

                if (filename.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                  return (
                    <div key={idx} className="media-item">
                      <img
                        src={url}
                        alt={`${productName} - ${variant.couleur} image ${
                          idx + 1
                        }`}
                      />
                    </div>
                  );
                }

                if (filename.match(/\.(glb|gltf|obj)$/i)) {
                  return (
                    <div key={idx} className="media-item model-viewer">
                      <ThreeModelViewer url={url} width={120} height={120} />
                    </div>
                  );
                }

                return (
                  <div key={idx} className="media-item unsupported">
                    Format non supporté
                  </div>
                );
              })
            ) : (
              <p className="no-media">Aucune image disponible</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
function formatProductType(type) {
  return type
    .replace("collections speciales", "Collections Spéciales")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function getColorHex(color) {
  const colors = {
    noir: "#222222",
    blanc: "#f0f0f0",
    beige: "#f5f5dc",
    rouge: "#dc2626",
    bleu: "#2563eb",
    vert: "#16a34a",
    jaune: "#ca8a04",
    rose: "#db2777",
    violet: "#7c3aed",
    gris: "#6b7280",
    orange: "#ea580c",
  };
  return colors[color.toLowerCase()] || "#666";
}

// Styles
const styles = `
:root {
  --primary: #4f46e5;
  --primary-light: #6366f1;
  --primary-dark: #4338ca;
  --secondary: #f97316;
  --success: #10b981;
  --danger: #ef4444;
  --warning: #f59e0b;
  --info: #3b82f6;
  --light: #f8fafc;
  --dark: #1e293b;
  --gray: #64748b;
  --gray-light: #e2e8f0;
  --white: #ffffff;
  --shadow-sm: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  --shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  --shadow-md: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  --radius-sm: 0.25rem;
  --radius: 0.5rem;
  --radius-lg: 0.75rem;
  --transition: all 0.2s ease;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  line-height: 1.5;
  color: var(--dark);
  background-color: var(--light);
}

.products-admin-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
}

.products-header {
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.products-header h2 {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--dark);
}

.search-container {
  position: relative;
  max-width: 500px;
  width: 100%;
}

.search-container input {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border: 1px solid var(--gray-light);
  border-radius: var(--radius);
  font-size: 0.95rem;
  transition: var(--transition);
  background-color: var(--white);
}

.search-container input:focus {
  outline: none;
  border-color: var(--primary-light);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray);
  font-size: 0.9rem;
}

.products-count {
  font-size: 0.9rem;
  color: var(--gray);
}

.products-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

.product-card {
  background-color: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow);
  overflow: hidden;
  transition: var(--transition);
}

.product-card:hover {
  box-shadow: var(--shadow-md);
}

.product-card-header {
  padding: 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: var(--transition);
}

.product-basic-info {
  flex: 1;
  min-width: 0;
}

.product-basic-info h3 {
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-meta {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.price-container {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.original-price {
  font-size: 0.9rem;
  color: var(--gray);
  text-decoration: line-through;
  position: relative;
}

.normal-price {
  font-weight: 700;
  font-size: 1rem;
  color: var(--dark);
}

.promo-price-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.promo-price {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--danger);
}

.promo-badge {
  background-color: var(--danger);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.tag-icon {
  font-size: 0.65rem;
}

.product-type {
  font-size: 0.8rem;
  color: var(--gray);
  background-color: var(--gray-light);
  padding: 0.25rem 0.5rem;
  border-radius: var(--radius-sm);
  text-transform: capitalize;
}

.product-actions-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.popular-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--gray-light);
  font-size: 1.1rem;
  transition: var(--transition);
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popular-btn.active {
  color: var(--warning);
}

.popular-btn:hover {
  transform: scale(1.1);
}

.expand-btn {
  color: var(--gray);
  font-size: 0.9rem;
  transition: var(--transition);
}

.product-card-details {
  padding: 0 1.25rem 1.25rem;
  border-top: 1px solid var(--gray-light);
}

.product-description {
  margin: 1rem 0;
  color: var(--dark);
  font-size: 0.95rem;
  line-height: 1.6;
}

.no-description {
  color: var(--gray);
  font-style: italic;
}

.variants-section {
  margin-top: 1.5rem;
}

.variants-section h4 {
  font-size: 1rem;
  margin-bottom: 1rem;
  color: var(--dark);
}

.variant-card {
  margin-bottom: 1.5rem;
  padding: 1rem;
  border-radius: var(--radius);
  background-color: var(--light);
  border-left: 4px solid;
}

.variant-header h5 {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  text-transform: capitalize;
}

.variant-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.stock-section h6, .variant-media h6 {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  color: var(--dark);
}

.stock-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
}

.stock-item {
  background-color: var(--white);
  padding: 0.75rem;
  border-radius: var(--radius-sm);
  font-size: 0.85rem;
  box-shadow: var(--shadow-sm);
}

.stock-item span {
  display: flex;
  justify-content: space-between;
}

.stock-item strong {
  font-weight: 600;
}

.no-stock {
  color: var(--gray);
  font-style: italic;
  font-size: 0.9rem;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 1rem;
}

.media-item {
  position: relative;
  height: 100px;
  border-radius: var(--radius-sm);
  overflow: hidden;
  background-color: var(--gray-light);
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: var(--transition);
  cursor: zoom-in;
}

.media-item:hover img {
  transform: scale(1.8);
  z-index: 10;
}

.model-viewer {
  background-color: var(--white);
  border: 1px solid var(--gray-light);
}

.unsupported {
  font-size: 0.8rem;
  color: var(--gray);
  text-align: center;
  padding: 1rem;
}

.no-media {
  color: var(--gray);
  font-style: italic;
  font-size: 0.9rem;
}

.no-variants {
  color: var(--gray);
  font-style: italic;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem 0;
}

.product-actions-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--gray-light);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  gap: 1rem;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
  border: 3px solid var(--gray-light);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-message {
  text-align: center;
  color: var(--danger);
  background-color: #fee2e2;
  padding: 1rem;
  border-radius: var(--radius);
  max-width: 600px;
  margin: 2rem auto;
  font-weight: 500;
}

@media (max-width: 768px) {
  .products-admin-container {
    padding: 1.5rem 1rem;
  }

  .stock-grid {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  }

  .media-grid {
    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  }
}

@media (max-width: 480px) {
  .product-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .product-actions-header {
    width: 100%;
    justify-content: space-between;
  }

  .product-actions-footer {
    flex-direction: column;
  }
}
`;

// Inject styles into head
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);